(function () {
  const PRODUCT_CONTEXT_SELECTORS = [
    'details[data-product-id]',
    '.product__quickview-wrapper',
    '.product__section',
    '.js-upsell-wrapper',
    '[data-section-type="product-grid-add"]'
  ].join(', ');

  const SIZE_NAME_PATTERN = /(size|talla|taille|taglia|groesse|grosse|saiz|kich\s*co)/i;
  const SIZE_VALUE_PATTERN = /^(xxxs|xxs|xs|s|m|l|xl|xxl|xxxl|xxxxl|small|medium|large|one size|os|o\/s|\d{1,2}(?:\.\d)?|\d{2}[a-z]?)$/i;

  const queuedUpdates = new WeakMap();
  const productCache = new WeakMap();
  let cartConfig;

  function getCartConfig() {
    if (cartConfig) {
      return cartConfig;
    }

    const configElement = document.getElementById('cart-config');

    if (!configElement) {
      cartConfig = {};
      return cartConfig;
    }

    try {
      cartConfig = JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      cartConfig = {};
    }

    return cartConfig;
  }

  function findContextFromScript(script) {
    return script.closest(PRODUCT_CONTEXT_SELECTORS);
  }

  function findNearestContext(element) {
    if (!(element instanceof Element)) {
      return null;
    }

    let current = element;

    while (current && current !== document.body) {
      if (current.matches?.(PRODUCT_CONTEXT_SELECTORS) && current.querySelector('.product-json')) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  function findContextsWithin(root) {
    const contexts = new Set();

    if (!(root instanceof Element) && !(root instanceof Document) && !(root instanceof DocumentFragment)) {
      return contexts;
    }

    if (root instanceof Element) {
      const directContext = findNearestContext(root);

      if (directContext) {
        contexts.add(directContext);
      }
    }

    root.querySelectorAll?.('.product-json').forEach((script) => {
      const context = findContextFromScript(script);

      if (context && context.querySelector('.js-ajax-submit')) {
        contexts.add(context);
      }
    });

    return contexts;
  }

  function getProduct(context) {
    const productScript = context.querySelector('.product-json');

    if (!productScript) {
      return null;
    }

    const cachedProduct = productCache.get(productScript);

    if (cachedProduct && cachedProduct.source === productScript.textContent) {
      return cachedProduct.data;
    }

    try {
      const product = JSON.parse(productScript.textContent || '{}');
      productCache.set(productScript, {
        source: productScript.textContent,
        data: product
      });
      return product;
    } catch (error) {
      return null;
    }
  }

  function getSelectedOptionValues(context, product) {
    const selectedValues = new Array(product.options?.length || 0).fill(null);
    const swatchGroups = Array.from(context.querySelectorAll('.swatches__container[data-option-index]'));

    if (swatchGroups.length) {
      swatchGroups.forEach((group) => {
        const optionIndex = parseInt(group.dataset.optionIndex || '0', 10) - 1;

        if (optionIndex < 0) {
          return;
        }

        const checkedInput = group.querySelector('input[type="radio"]:checked');
        selectedValues[optionIndex] = checkedInput?.value || null;
      });

      return selectedValues;
    }

    const selects = Array.from(context.querySelectorAll('select.js-variant-selector'));

    if (selects.length) {
      selects.forEach((select, fallbackIndex) => {
        const selectedOption = select.selectedOptions?.[0];
        const optionIndex = parseInt(selectedOption?.dataset.position || String(fallbackIndex + 1), 10) - 1;

        if (optionIndex < 0) {
          return;
        }

        selectedValues[optionIndex] = select.value || null;
      });

      return selectedValues;
    }

    return selectedValues;
  }

  function getExactVariant(product, selectedValues) {
    if (!selectedValues.length || selectedValues.some((value) => !value)) {
      return null;
    }

    return product.variants?.find((variant) => {
      return variant.options?.every((option, index) => option === selectedValues[index]);
    }) || null;
  }

  function getMatchingVariants(product, selectedValues) {
    return product.variants?.filter((variant) => {
      return variant.options?.every((option, index) => {
        return !selectedValues[index] || selectedValues[index] === option;
      });
    }) || [];
  }

  function getOptionValues(product, optionIndex) {
    return Array.from(new Set(
      (product.variants || [])
        .map((variant) => variant.options?.[optionIndex])
        .filter(Boolean)
    ));
  }

  function isSizeOption(product, optionIndex) {
    const optionName = product.options?.[optionIndex] || '';

    if (SIZE_NAME_PATTERN.test(optionName)) {
      return true;
    }

    const optionValues = getOptionValues(product, optionIndex);

    if (optionValues.length < 2) {
      return false;
    }

    const sizeLikeValueCount = optionValues.filter((value) => {
      return SIZE_VALUE_PATTERN.test(String(value).trim());
    }).length;

    return sizeLikeValueCount >= 2;
  }

  function setButtonLabel(button, label) {
    if (button.tagName === 'INPUT') {
      button.value = label;
      return;
    }

    button.textContent = label;
  }

  function getAtcButtons(context) {
    return Array.from(context.querySelectorAll('.js-ajax-submit')).filter((button) => {
      return button.matches('input[type="submit"], input[type="button"], button');
    });
  }

  function applyStateToButtons(buttons, state) {
    buttons.forEach((button) => {
      setButtonLabel(button, state.label);
      button.disabled = state.disabled;
      button.dataset.stickyAction = state.stickyAction || 'submit';
    });
  }

  function determineState(context) {
    const config = getCartConfig();
    const product = getProduct(context);

    if (!product?.variants?.length || !product.options?.length) {
      return null;
    }

    const selectedValues = getSelectedOptionValues(context, product);
    const exactVariant = getExactVariant(product, selectedValues);

    if (exactVariant) {
      return {
        label: exactVariant.available ? config.add_to_cart : config.sold_out,
        disabled: !exactVariant.available,
        stickyAction: 'submit'
      };
    }

    if (!selectedValues.some(Boolean)) {
      return null;
    }

    const missingSize = selectedValues.some((value, index) => !value && isSizeOption(product, index));

    if (!missingSize) {
      return null;
    }

    const matchingVariants = getMatchingVariants(product, selectedValues);

    if (!matchingVariants.length) {
      return {
        label: config.unavailable,
        disabled: true
      };
    }

    const hasAvailableMatch = matchingVariants.some((variant) => variant.available);

    return {
      label: hasAvailableMatch ? config.select_size : config.sold_out,
      disabled: true,
      stickyAction: hasAvailableMatch ? 'select-size' : 'submit'
    };
  }

  function syncStickyButton() {
    const stickyButton = document.querySelector('.js-custom-sticky-atc');

    if (!stickyButton) {
      return;
    }

    const targetFormId = stickyButton.dataset.targetFormId;
    const escapedFormId = targetFormId && window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(targetFormId)
      : targetFormId;
    const mainButton = escapedFormId
      ? document.querySelector(`#${escapedFormId} .AddtoCart`)
      : document.querySelector('.js-prod-form-submit .AddtoCart');

    if (!stickyButton || !mainButton) {
      return;
    }

    const label = mainButton.tagName === 'INPUT' ? mainButton.value : mainButton.textContent.trim();
    const stickyAction = mainButton.dataset.stickyAction || 'submit';

    stickyButton.textContent = label;
    stickyButton.dataset.stickyAction = stickyAction;
    stickyButton.disabled = stickyAction === 'select-size' ? false : mainButton.disabled;
    stickyButton.classList.toggle('is-select-size', stickyAction === 'select-size');
  }

  function scheduleStickySyncBurst() {
    [0, 75, 300, 1000, 3200].forEach((delay) => {
      window.setTimeout(syncStickyButton, delay);
    });
  }

  function updateContext(context) {
    const buttons = getAtcButtons(context);

    if (!buttons.length) {
      syncStickyButton();
      return;
    }

    buttons.forEach((button) => {
      button.dataset.stickyAction = 'submit';
    });

    const state = determineState(context);

    if (state) {
      applyStateToButtons(buttons, state);
    }

    syncStickyButton();
  }

  function queueContextUpdate(context, delay = 0) {
    if (!context) {
      return;
    }

    const existingTimer = queuedUpdates.get(context);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timerId = window.setTimeout(() => {
      queuedUpdates.delete(context);
      updateContext(context);
    }, delay);

    queuedUpdates.set(context, timerId);
  }

  function updateAllContexts(root = document) {
    findContextsWithin(root).forEach((context) => {
      queueContextUpdate(context);
    });

    syncStickyButton();
  }

  document.addEventListener('change', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('.js-variant-selector')
      : null;

    if (!trigger) {
      return;
    }

    const context = findNearestContext(trigger);
    queueContextUpdate(context);
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('.js-prod-form-submit .AddtoCart, .js-custom-sticky-atc')
      : null;

    if (!trigger) {
      return;
    }

    scheduleStickySyncBurst();
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateAllContexts(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        findContextsWithin(mutation.target).forEach((context) => {
          queueContextUpdate(context, 0);
        });

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          findContextsWithin(node).forEach((context) => {
            queueContextUpdate(context, 0);
          });
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
})();
